/**
 * Per-user history of completed Practice Arena sessions.
 *
 * Backed by `GET/POST/DELETE /mindspace/practice/sessions` on the
 * FastAPI backend. The sidebar reads via `usePracticeHistory()`; the
 * orchestrator writes via `useRecordPracticeSession().mutate(...)`.
 * Cross-tab sync comes from React Query's `refetchOnWindowFocus`
 * default - focusing another tab re-pulls the list.
 *
 * Capped at 50 most-recent records per user (enforced on the server).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  mindspaceApi,
  type CreatePracticeSessionPayload,
  type PracticeQuestionAttemptDto,
  type PracticeSessionDto,
  type PracticeSessionOutcomeDto,
  type PracticeStats,
} from '@/lib/api-client';
import { useDataMode } from '@/contexts/DataModeContext';
import { parseUtcDate } from '@/lib/datetime';
import { notify } from '@/lib/notify';
import type {
  PracticeMode,
  PracticeScope,
  QuestionAttempt,
  SessionOutcome,
} from '@/lib/practice/session-types';

// ---------------------------------------------------------------------------
// SessionRecord - the frontend's canonical history shape (camelCase, unix ms)
// ---------------------------------------------------------------------------

export interface SessionRecord {
  /** Backend Mongo `_id`, stringified. Used as React key + URL selector. */
  id: string;
  /** When the session ended, in unix ms (client-friendly for date math). */
  endedAt: number;
  mode: PracticeMode;
  scope: PracticeScope;
  total: number;
  mastered: number;
  improved: number;
  weak: number;
  durationMs: number;
  outcomes: SessionOutcome[];
  /** True when the user abandoned the session mid-run. */
  incomplete?: boolean;
}

// What `useRecordPracticeSession` accepts - the orchestrator already has
// `endedAt` (it tracks session start/end timestamps) but doesn't need to
// supply `id`; the server stamps that on insert.
//
// `clientSessionId` is a UUID minted by the orchestrator at session-start
// time. The backend upserts on `(user_id, client_session_id)` so retries
// (network blip, partial-flush-then-finish) never create duplicate rows.
export type NewSessionPayload = Omit<SessionRecord, 'id' | 'endedAt'> & {
  clientSessionId?: string;
};

// ---------------------------------------------------------------------------
// DTO ↔ SessionRecord mappers
// ---------------------------------------------------------------------------

function attemptFromDto(a: PracticeQuestionAttemptDto): QuestionAttempt {
  return {
    question: a.question,
    correctAnswer: a.correct_answer,
    explanation: a.explanation ?? undefined,
    options: a.options ?? undefined,
    pickedAnswer: a.picked_answer ?? undefined,
    wasCorrect: a.was_correct ?? undefined,
  };
}

function outcomeFromDto(o: PracticeSessionOutcomeDto): SessionOutcome {
  return {
    conceptId: o.concept_id,
    conceptName: o.concept_name,
    grade: o.grade,
    retention: o.retention,
    attempts: o.attempts.map(attemptFromDto),
  };
}

function fromDto(dto: PracticeSessionDto): SessionRecord {
  return {
    id: dto.id,
    endedAt: parseUtcDate(dto.ended_at).getTime(),
    mode: dto.mode,
    scope: dto.scope,
    total: dto.total,
    mastered: dto.mastered,
    improved: dto.improved,
    weak: dto.weak,
    durationMs: dto.duration_ms,
    incomplete: dto.incomplete,
    outcomes: dto.outcomes.map(outcomeFromDto),
  };
}

function attemptToDto(a: QuestionAttempt): PracticeQuestionAttemptDto {
  return {
    question: a.question,
    correct_answer: a.correctAnswer,
    explanation: a.explanation ?? null,
    options: a.options ?? null,
    picked_answer: a.pickedAnswer ?? null,
    was_correct: a.wasCorrect ?? null,
  };
}

function outcomeToDto(o: SessionOutcome): PracticeSessionOutcomeDto {
  return {
    concept_id: o.conceptId,
    concept_name: o.conceptName,
    grade: o.grade,
    retention: o.retention,
    attempts: (o.attempts ?? []).map(attemptToDto),
  };
}

function payloadToDto(
  payload: NewSessionPayload,
  endedAt: number
): CreatePracticeSessionPayload {
  return {
    client_session_id: payload.clientSessionId,
    mode: payload.mode,
    scope: payload.scope,
    ended_at: new Date(endedAt).toISOString(),
    duration_ms: payload.durationMs,
    total: payload.total,
    mastered: payload.mastered,
    improved: payload.improved,
    weak: payload.weak,
    incomplete: payload.incomplete ?? false,
    outcomes: payload.outcomes.map(outcomeToDto),
  };
}

// ---------------------------------------------------------------------------
// React Query hooks
// ---------------------------------------------------------------------------

const practiceHistoryKey = ['mindspace', 'practice', 'sessions'] as const;

/** Shown when a session record POST fails and its optimistic row rolls back. */
export const SAVE_FAILED_TOAST = {
  title: "Couldn't save this session",
  description:
    "It won't appear in your history. Check your connection and try again later.",
} as const;

function makeTempId(): string {
  return `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function usePracticeHistoryQuery() {
  const { isMock, isEmpty } = useDataMode();
  return useQuery<SessionRecord[]>({
    queryKey: practiceHistoryKey,
    queryFn: async () => {
      // In `empty` and `mock` modes the list starts blank. Mock-mode
      // sessions that the user records inside one app run still appear
      // in the sidebar via the optimistic insert in the record mutation
      // - they just don't persist across reloads. This keeps the seed
      // surface honest with the post-cutover backend behavior.
      if (isEmpty || isMock) return [];
      const dtos = await mindspaceApi.listPracticeSessions(50);
      return dtos.map(fromDto);
    },
    staleTime: 30 * 1000,
  });
}

export function usePracticeHistory(): SessionRecord[] {
  return usePracticeHistoryQuery().data ?? [];
}

/**
 * Like `usePracticeHistory` but also surfaces the initial-load state so a
 * consumer can render a shimmer instead of flashing its empty state before
 * the first fetch resolves. `isLoading` is true only on the first load with
 * no cached data (React Query's pending+fetching), so a populated cache
 * reveals instantly without a skeleton flash.
 */
export function usePracticeHistoryState(): {
  sessions: SessionRecord[];
  isLoading: boolean;
  /** The read failed with nothing cached — NOT an empty history. A failed
   *  refetch keeps `data`, so it never sets this. */
  loadFailed: boolean;
  retry: () => void;
} {
  const { data, isLoading, isError, refetch } = usePracticeHistoryQuery();
  return {
    sessions: data ?? [],
    isLoading,
    loadFailed: isError && !data,
    retry: () => void refetch(),
  };
}

const EMPTY_STATS: PracticeStats = {
  streak_current: 0,
  streak_best: 0,
  today_sessions: 0,
  today_concepts: 0,
  today_minutes: 0,
};

/**
 * Practice Arena header stats (streak + today's activity), computed on the
 * backend. The streak is the durable learning streak (meaningful StudySession
 * days, all-time) - NOT derived from the 50-capped session log - and "today"
 * counts distinct concepts. Shares the `['mindspace']` key prefix, so the
 * post-session invalidate in the orchestrator refreshes it automatically.
 */
export function usePracticeStats() {
  const { isMock, isEmpty } = useDataMode();
  return useQuery<PracticeStats>({
    queryKey: ['mindspace', 'practice', 'stats'],
    queryFn: async () => {
      if (isEmpty || isMock) return EMPTY_STATS;
      try {
        return await mindspaceApi.getPracticeStats();
      } catch {
        // Header stats are non-critical chrome - degrade to zeros (both
        // badges hide at 0) rather than surfacing an error.
        return EMPTY_STATS;
      }
    },
    staleTime: 60 * 1000,
  });
}

/**
 * Append a session to the head of the history list (most-recent first).
 *
 * Optimistic - the new row appears in the sidebar before the POST
 * round-trips. On error the optimistic row rolls back and the query
 * cache restores the prior list.
 */
export function useRecordPracticeSession() {
  const qc = useQueryClient();
  const { isMock, isEmpty } = useDataMode();
  return useMutation<
    SessionRecord,
    Error,
    NewSessionPayload,
    { previous: SessionRecord[] }
  >({
    mutationFn: async payload => {
      const endedAt = Date.now();
      if (isEmpty || isMock) {
        // No backend round-trip in non-API modes. Return a synthetic
        // record so the optimistic insert settles cleanly.
        return {
          ...payload,
          id: makeTempId(),
          endedAt,
        };
      }
      const dto = await mindspaceApi.createPracticeSession(
        payloadToDto(payload, endedAt)
      );
      return fromDto(dto);
    },
    onMutate: async payload => {
      await qc.cancelQueries({ queryKey: practiceHistoryKey });
      const previous =
        qc.getQueryData<SessionRecord[]>(practiceHistoryKey) ?? [];
      const optimistic: SessionRecord = {
        ...payload,
        id: makeTempId(),
        endedAt: Date.now(),
      };
      qc.setQueryData<SessionRecord[]>(practiceHistoryKey, [
        optimistic,
        ...previous,
      ]);
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(practiceHistoryKey, ctx.previous);
      }
      // The optimistic row just vanished from the sidebar; say so. Defining
      // `onError` here replaces the client's default mutation toast, which is
      // how this rollback used to be silent.
      notify.errorWithDetail(SAVE_FAILED_TOAST);
    },
    onSettled: () => {
      // Server-authoritative refetch so the temp id is replaced with
      // the real one and the 50-cap trim is reflected.
      qc.invalidateQueries({ queryKey: practiceHistoryKey });
    },
  });
}

/** Drop every practice session for the current user. */
export function useClearPracticeHistory() {
  const qc = useQueryClient();
  const { isMock, isEmpty } = useDataMode();
  return useMutation<void, Error, void>({
    mutationFn: async () => {
      if (isEmpty || isMock) return;
      await mindspaceApi.clearPracticeSessions();
    },
    onSuccess: () => {
      qc.setQueryData<SessionRecord[]>(practiceHistoryKey, []);
    },
  });
}
